import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  Theme,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Field from "components/Field";
import FieldRichText from "components/FieldRichText";
import { makeStyles } from "helpers/StyleHelper";
import { FormattedMessage as T, useIntl } from "react-intl";
import { ManualGradingUserAnswer } from "services/KnowfullyAdminClient";
import { QuestionTypeIdEnum } from "types/common-types";

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    border: "1px solid grey",
  },
  spacing1: {
    mt: theme.spacing(1),
  },
  formLabel: {
    color: "black",
    fontSize: "large",
    fontWeight: "bold",
  },
  questionBox: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.secondary.main,
    color: "white",
    fontWeight: "bold",
  },
  fileName: {
    marginBottom: theme.spacing(2),
  },
}));

type ManualGradingAnswerProps = {
  disabled?: boolean;
  fieldName: string;
  userAnswer: ManualGradingUserAnswer;
};

const ManualGradingAnswer = ({
  disabled,
  fieldName,
  userAnswer,
}: ManualGradingAnswerProps) => {
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();

  const renderFilePreview = () => {
    switch (userAnswer.questionTypeId) {
      case QuestionTypeIdEnum.TextQuestionId:
        return userAnswer.userAnswer ?? "-";
      case QuestionTypeIdEnum.FileUploadVideoId:
        return (
          <Box sx={styles.spacing1}>
            <video controls width="100%">
              <source src={userAnswer.fileUrl} />
              <T id="browserDoesNotSupportVideo" />
              <a href={userAnswer.fileUrl} target="_blank" download>
                {t({ id: "downloadHere" })}
              </a>
            </video>
          </Box>
        );
      case QuestionTypeIdEnum.FileUploadImageId:
        return (
          <Box sx={styles.spacing1}>
            <img
              src={userAnswer.fileUrl}
              alt={t({ id: "userAnswerImageAlt" })}
              width="100%"
            />
          </Box>
        );
      case QuestionTypeIdEnum.FileUploadDocumentId:
      default:
        return userAnswer.userAnswer?.endsWith(".pdf") ? (
          <Box sx={styles.spacing1}>
            <object data={userAnswer.fileUrl} width="100%" height="500px">
              <T id="userCannotViewPdf" />
              <a href={userAnswer.fileUrl} target="_blank" download>
                {t({ id: "downloadHere" })}
              </a>
            </object>
          </Box>
        ) : (
          <Box sx={styles.spacing1}>
            <Typography sx={styles.fileName}>
              File Name: {userAnswer.fileName}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              component="a"
              target="_blank"
              href={userAnswer.fileUrl}
              download
            >
              <T id="download" />
            </Button>
          </Box>
        );
    }
  };

  return (
    <Box my={2} sx={styles.container}>
      <Box sx={styles.questionBox}>
        {t({ id: "questionSequence" }, { sequence: userAnswer.sequence })}
        {userAnswer.question && (
          <span
            dangerouslySetInnerHTML={{ __html: userAnswer.question }}
          ></span>
        )}
      </Box>

      <Grid container spacing={2} p={2}>
        <Grid item xs={6}>
          <Typography sx={styles.formLabel}>
            <T id="userResponseLabel" />
          </Typography>
          {renderFilePreview()}
        </Grid>
        <Field name={`${fieldName}.isCorrect`} required>
          {({ input, meta }) => (
            <Grid item xs={6}>
              <FormControl size="small" error={meta.error && meta.submitFailed}>
                <FormLabel sx={styles.formLabel}>
                  <T id="doesResponseMeetRequirements" />
                </FormLabel>

                <Select
                  {...input}
                  disabled={disabled}
                  sx={styles.spacing1}
                  variant="outlined"
                >
                  <MenuItem value={true as any}>
                    <T id="meetRequirements" />
                  </MenuItem>
                  <MenuItem value={false as any}>
                    <T id="doesNotMeetRequirements" />
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
        </Field>
      </Grid>

      <hr />
      <Grid container spacing={2} p={2}>
        <Grid item xs={6}>
          <Typography pb={1} fontWeight="bold">
            <T id="expectedAnswerLabel" />
          </Typography>
          {userAnswer.expectedAnswer && (
            <span
              dangerouslySetInnerHTML={{ __html: userAnswer.expectedAnswer }}
            ></span>
          )}
        </Grid>
        <Field name={`${fieldName}.feedback`}>
          {({ input, meta }) => (
            <Grid item xs={6}>
              <Typography pb={1} fontWeight="bold">
                <T id="feedbackFormLabel" />
              </Typography>
              <Typography>
                <T id="feedbackFormInfo" />
              </Typography>

              <FieldRichText
                input={input}
                meta={meta}
                init={{
                  height: 200,
                }}
              />
            </Grid>
          )}
        </Field>
      </Grid>
    </Box>
  );
};

export default ManualGradingAnswer;
